import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Última actualización: febrero 2026

Tu privacidad es importante para nosotros. Esta política describe cómo recopilamos, usamos y protegemos tu información.

Información que recopilamos
• Información de pago: procesada de forma segura por nuestro proveedor de pagos. No almacenamos datos de tarjetas.
• Correo electrónico: solo si lo proporcionas voluntariamente para recibir tu compra.

Uso de la información
• Entregar el producto digital que compraste.
• Enviar actualizaciones relacionadas con tu compra (si aplica).
• No vendemos, alquilamos ni compartimos tu información personal con terceros.

Cookies
• Usamos cookies básicas para el funcionamiento del sitio y análisis anónimos de tráfico.

Tus derechos
• Puedes solicitar la eliminación de tus datos en cualquier momento.
• Puedes solicitar una copia de la información que tenemos sobre ti.

Seguridad
• Utilizamos medidas de seguridad estándar de la industria para proteger tu información.

Cambios a esta política
• Nos reservamos el derecho de actualizar esta política. Los cambios se publicarán en esta página.
`.trim();
const termsContent = `
Última actualización: febrero 2026

Al comprar y descargar el Plan Antipánico, aceptas los siguientes términos.

Producto digital
• El Plan Antipánico es un producto educativo en formato digital (PDF + plantillas).
• No constituye asesoramiento financiero, legal o profesional.
• Los resultados pueden variar según tu situación personal.

Licencia de uso
• Recibes una licencia personal e intransferible.
• No está permitido redistribuir, revender o compartir el contenido.

Pagos y reembolsos
• El precio se muestra en dólares estadounidenses (USD).
• Ofrecemos una garantía de reembolso de 7 días. Si no te fue útil, puedes solicitar un reembolso completo sin preguntas.

Propiedad intelectual
• Todo el contenido del Plan Antipánico está protegido por derechos de autor.
• Las plantillas son para uso personal únicamente.

Limitación de responsabilidad
• No nos hacemos responsables por decisiones financieras tomadas a partir de este material.
• Este producto es una herramienta educativa, no un sustituto de asesoría profesional.

Cambios a estos términos
• Nos reservamos el derecho de modificar estos términos. Los cambios se publicarán en esta página.
`.trim();
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-12">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#4A3728]/60 backdrop-blur-sm" data-id="element-13" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onClick={(e: React.MouseEvent) => e.stopPropagation()} className="relative bg-[#FAF7F2] rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden z-10" data-id="element-14">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D9]" data-id="element-15">
          <h2 className="text-xl font-serif font-bold text-[#4A3728]" data-id="element-16">
            {title}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#E8E2D9] transition-colors text-[#6B5D52]" data-id="element-17">
            <X className="w-5 h-5" data-id="element-18" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)]" data-id="element-19">
          {content.split('\n\n').map((paragraph, i) => {
          if (paragraph.startsWith('•')) {
            return <ul key={i} className="mb-4 space-y-1.5" data-id="element-20">
                  {paragraph.split('\n').map((line, j) => <li key={j} className="text-sm text-[#6B5D52] pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-[#C4956A]/50" data-id="element-21">
                      {line.replace('• ', '')}
                    </li>)}
                </ul>;
          }
          const isHeading = !paragraph.startsWith('•') && paragraph.length < 60 && !paragraph.includes('.');
          if (isHeading && i > 0) {
            return <h3 key={i} className="font-serif font-bold text-[#4A3728] mt-6 mb-2 text-base" data-id="element-22">
                  {paragraph}
                </h3>;
          }
          return <p key={i} className="text-sm text-[#6B5D52] mb-3 leading-relaxed" data-id="element-23">
                {paragraph}
              </p>;
        })}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#4A3728] text-[#FAF7F2] py-10 text-center text-sm" data-id="element-24">
        <div className="max-w-4xl mx-auto px-6" data-id="element-25">
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-26">
            <button onClick={() => setActiveModal('privacy')} className="opacity-70 hover:opacity-100 transition-opacity underline underline-offset-2" data-id="element-27">
              Política de Privacidad
            </button>
            <span className="opacity-30" data-id="element-28">|</span>
            <button onClick={() => setActiveModal('terms')} className="opacity-70 hover:opacity-100 transition-opacity underline underline-offset-2" data-id="element-29">
              Términos y Condiciones
            </button>
          </div>
          <p className="opacity-50 mb-3" data-id="element-30">
            © 2026 Plan Antipánico. Todos los derechos reservados.
          </p>
          <p className="opacity-30 text-xs max-w-lg mx-auto" data-id="element-31">
            Este producto es educativo y no constituye asesoramiento financiero
            profesional. Los resultados pueden variar según tu situación
            personal.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-32">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-33" />}
      </AnimatePresence>
    </>;
}